import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NotificationCompositionRequest } from 'src/api/FiretrolApiClient';
import { notificationsApiClient } from 'src/api/FiretrolApiClientInstances';
import { invalidateFor } from 'src/api/queries/invalidationMap';

export interface IWorkflowNotificationTarget {
  resourceType: string;
  jobId: number;
  entityId: string;
}

type CompositionArgs = IWorkflowNotificationTarget & {
  request: NotificationCompositionRequest;
};

const invalidateHistory = (
  qc: ReturnType<typeof useQueryClient>,
  target: IWorkflowNotificationTarget,
) => invalidateFor('workflowNotification.send', qc, target);

export function useSendWorkflowNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ resourceType, jobId, entityId, request }: CompositionArgs) =>
      notificationsApiClient.send(resourceType, jobId, entityId, request),
    meta: { errorContext: 'notification send' },
    onSettled: (_data, _err, target) => invalidateHistory(qc, target),
  });
}

export function useSkipWorkflowNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ resourceType, jobId, entityId, request }: CompositionArgs) =>
      notificationsApiClient.skip(resourceType, jobId, entityId, request),
    meta: { errorContext: 'notification skip' },
    onSettled: (_data, _err, target) => invalidateHistory(qc, target),
  });
}

export function useResendWorkflowNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      resourceType,
      jobId,
      entityId,
      logId,
    }: IWorkflowNotificationTarget & { logId: string }) =>
      notificationsApiClient.resend(resourceType, jobId, entityId, logId),
    meta: { errorContext: 'notification resend' },
    onSettled: (_data, _err, target) => invalidateHistory(qc, target),
  });
}

/**
 * The preview is on demand and sequenced (an external pass may follow the
 * internal one), so it stays a mutation rather than a cached query.
 */
export function usePreviewNotification() {
  return useMutation({
    mutationFn: ({
      resourceType,
      jobId,
      entityId,
      eventType,
      external,
    }: IWorkflowNotificationTarget & {
      eventType?: string;
      external?: boolean;
    }) =>
      notificationsApiClient.preview(
        resourceType,
        jobId,
        entityId,
        eventType,
        external,
      ),
    meta: { suppressErrorToast: true, errorContext: 'notification preview' },
  });
}
